import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PricingComparisonTable from '../components/PricingComparisonTable';
import { XMarkIcon } from '../design-system/icons';
import useMediaQuery from '../hooks/useMediaQuery';

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  roofSoft:   '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', r5: '20px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sh3: '0 3px 0 rgba(46,33,9,0.05), 0 20px 36px -12px rgba(46,33,9,0.18)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Stripe promise will be loaded dynamically
let stripePromise = null;

const Pricing = () => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const {
    subscription,
    plans,
    fetchPlans,
    createEmbeddedCheckout,
    getStripeConfig,
    error: subError
  } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [faqOpen, setFaqOpen] = useState(null);

  // Check for canceled checkout
  const canceled = searchParams.get('canceled') === 'true';
  const success = searchParams.get('success') === 'true';

  // Initialize Stripe
  useEffect(() => {
    const initStripe = async () => {
      if (!stripePromise) {
        try {
          const config = await getStripeConfig();
          if (config?.publishable_key) {
            stripePromise = loadStripe(config.publishable_key);
          }
        } catch (err) {
          console.error('Failed to initialize Stripe:', err);
        }
      }
    };
    initStripe();
  }, [getStripeConfig]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleGetStarted = async (planId) => {
    if (!isAuthenticated()) {
      navigate('/', { state: { returnTo: '/pricing' } });
      return;
    }

    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }

    if (planId === 'enterprise') {
      window.location.href = 'mailto:sales@cahoots.cc?subject=Cahoots Enterprise Inquiry';
      return;
    }

    try {
      setCheckoutLoading(true);
      setSelectedPlanId(planId);
      const plan = plans.find(p => p.id === planId);
      if (!plan?.stripe_price_id) {
        alert('Payment system is not configured. Please contact support.');
        return;
      }

      // Create embedded checkout session with theme
      const result = await createEmbeddedCheckout(
        plan.stripe_price_id,
        undefined, // use default return URL
        isDark ? 'dark' : 'light'
      );
      if (result?.client_secret) {
        setClientSecret(result.client_secret);
        setShowCheckoutModal(true);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCloseCheckout = useCallback(() => {
    setShowCheckoutModal(false);
    setClientSecret(null);
    setSelectedPlanId(null);
  }, []);

  const currentTier = subscription?.tier || 'free';

  const tiers = [
    {
      id: 'free',
      name: 'FREE',
      price: '$0',
      priceSuffix: '/ forever',
      blurb: 'For solo builders kicking the tires.',
      features: [
        'Idea discovery and specs',
        'Features and stories',
        'Event modeling',
        'Gap detection',
        'Export to markdown',
      ],
      cta: currentTier === 'free' ? 'Current plan' : 'Get started free',
      ctaDisabled: currentTier === 'free',
      highlighted: false,
    },
    {
      id: 'hobbyist',
      name: 'HOBBYIST',
      price: '$10',
      priceSuffix: '/ month',
      blurb: 'For side projects and the ones who actually ship.',
      features: [
        'Everything in Free',
        'Export to JSON, CSV, YAML',
        'AGENTS.md bundle for AI coding',
        'Email support',
      ],
      cta: currentTier === 'hobbyist' ? 'Current plan' : 'Upgrade to Hobbyist',
      ctaDisabled: currentTier === 'hobbyist' || checkoutLoading,
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'PRO',
      price: '$50',
      priceSuffix: '/ month',
      blurb: 'For professional developers and real teams.',
      features: [
        'Everything in Hobbyist',
        'GitHub drift detection',
        'Jira + Trello integration',
        'Unlimited repos',
        'Priority support',
      ],
      cta: currentTier === 'pro' ? 'Current plan' : 'Start 7-day free trial',
      ctaDisabled: currentTier === 'pro' || checkoutLoading,
      highlighted: true,
      popular: true,
    },
    {
      id: 'enterprise',
      name: 'ENTERPRISE',
      price: "Let's talk",
      priceSuffix: '',
      blurb: 'Custom for your stack, policies, and scale.',
      features: [
        'Everything in Pro',
        'SSO + SCIM',
        'Self-hosted option',
        'Custom integrations',
        'Dedicated support',
      ],
      cta: currentTier === 'enterprise' ? 'Current plan' : 'Contact sales',
      ctaDisabled: currentTier === 'enterprise',
      highlighted: false,
    },
  ];

  const faqs = [
    {
      q: 'Can I cancel anytime?',
      a: 'Yes! You can cancel your subscription at any time. You\'ll continue to have access to Pro features until the end of your billing period.',
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept all major credit cards (Visa, Mastercard, American Express) and process payments securely through Stripe.',
    },
    {
      q: 'What happens when I upgrade?',
      a: 'When you upgrade, you\'ll immediately get access to all features for that tier including integrations, exports, and drift detection.',
    },
    {
      q: 'Do you offer refunds?',
      a: 'We offer a 14-day money-back guarantee. If you\'re not satisfied, contact us and we\'ll process a full refund.',
    },
    {
      q: 'Can I change plans later?',
      a: 'Absolutely! You can upgrade, downgrade, or cancel your plan at any time from your billing settings.',
    },
  ];

  const gridCols = isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: T.paper, fontFamily: T.sans }}>
      <SEO
        title="Pricing"
        description="Choose the plan that's right for your team. From free idea discovery to enterprise-grade features."
      />

      {/* Hero */}
      <div style={{ padding: '44px 48px 0', textAlign: 'center', marginBottom: '30px' }}>
        <span style={{
          display: 'inline-block',
          fontFamily: T.hand,
          fontSize: '26px',
          color: T.orange,
          transform: 'rotate(-1.5deg)',
          marginBottom: '6px',
        }}>
          honest pricing —
        </span>
        <h1 style={{
          fontFamily: T.sans,
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800,
          color: T.ink,
          letterSpacing: '-1px',
          margin: '0 0 14px',
        }}>
          Plans that grow with you.
        </h1>
        <p style={{
          fontSize: '16px',
          color: T.ink2,
          maxWidth: '540px',
          margin: '0 auto',
          lineHeight: 1.55,
        }}>
          Start free. Upgrade when you're in cahoots with more people, more repos, or bigger plans.
        </p>

        {canceled && (
          <div style={{
            display: 'inline-block',
            marginTop: '20px',
            padding: '12px 20px',
            borderRadius: T.r3,
            backgroundColor: T.roofSoft,
            border: `1px solid ${T.roof}`,
            fontSize: '14px',
            color: T.ink2,
          }}>
            Checkout was canceled. Feel free to try again when you're ready.
          </div>
        )}
        {success && (
          <div style={{
            display: 'inline-block',
            marginTop: '20px',
            padding: '12px 20px',
            borderRadius: T.r3,
            backgroundColor: T.mossSoft,
            border: `1px solid ${T.moss}`,
            fontSize: '14px',
            color: T.mossDark,
          }}>
            Thank you for subscribing! Your Pro features are now active.
          </div>
        )}
      </div>

      {/* Tier Cards */}
      <div style={{ padding: '0 48px 60px', maxWidth: '1196px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: '18px',
        }}>
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              loading={checkoutLoading && selectedPlanId === tier.id}
              onCta={() => handleGetStarted(tier.id)}
            />
          ))}
        </div>

        {subError && (
          <p style={{ textAlign: 'center', marginTop: '24px', color: T.capRed, fontSize: '14px' }}>
            {subError}
          </p>
        )}

        <p style={{
          textAlign: 'center',
          marginTop: '28px',
          fontSize: '13px',
          color: T.inkMuted,
          fontFamily: T.mono,
        }}>
          Pro includes a 7-day free trial · cancel anytime
        </p>
      </div>

      {/* Comparison Table */}
      <div style={{ backgroundColor: T.paperDeep, padding: '60px 48px' }}>
        <div style={{ maxWidth: '896px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <span style={{ fontFamily: T.hand, fontSize: '22px', color: T.orange }}>
              what's included —
            </span>
            <h2 style={{
              fontSize: '30px',
              fontWeight: 800,
              color: T.ink,
              margin: '4px 0 0',
              letterSpacing: '-0.5px',
            }}>
              Compare plans
            </h2>
          </div>
          <div style={{
            backgroundColor: T.card,
            borderRadius: T.r4,
            border: `1px solid ${T.rule}`,
            boxShadow: T.sh1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
            <PricingComparisonTable />
          </div>
          {isMobile && (
            <p style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: T.inkFaint }}>
              Scroll horizontally to see all plans
            </p>
          )}
        </div>
      </div>

      {/* FAQ */}
      <div style={{ padding: '60px 48px', maxWidth: '768px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <span style={{ fontFamily: T.hand, fontSize: '22px', color: T.orange }}>
            good questions —
          </span>
          <h2 style={{
            fontSize: '30px',
            fontWeight: 800,
            color: T.ink,
            margin: '4px 0 0',
            letterSpacing: '-0.5px',
          }}>
            Frequently asked
          </h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              question={faq.q}
              answer={faq.a}
              isOpen={faqOpen === i}
              onToggle={() => setFaqOpen(faqOpen === i ? null : i)}
            />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{
        backgroundColor: T.paperDeep,
        padding: '60px 48px',
        textAlign: 'center',
        borderTop: `1px solid ${T.rule}`,
      }}>
        <span style={{ fontFamily: T.hand, fontSize: '22px', color: T.orange }}>
          let's go —
        </span>
        <h2 style={{
          fontSize: '30px',
          fontWeight: 800,
          color: T.ink,
          margin: '4px 0 12px',
          letterSpacing: '-0.5px',
        }}>
          Ready to get started?
        </h2>
        <p style={{ fontSize: '15px', color: T.ink2, marginBottom: '24px' }}>
          Join developers using Cahoots to keep product and engineering in sync.
        </p>
        <CtaButton onClick={() => navigate(isAuthenticated() ? '/dashboard' : '/')}>
          {isAuthenticated() ? 'Go to Dashboard →' : 'Start for free →'}
        </CtaButton>
      </div>

      <Footer />

      {/* Embedded Checkout Modal */}
      {showCheckoutModal && clientSecret && stripePromise && (
        <CheckoutModal
          clientSecret={clientSecret}
          stripePromise={stripePromise}
          onClose={handleCloseCheckout}
          planName={plans.find(p => p.id === selectedPlanId)?.name || 'Pro'}
        />
      )}
    </div>
  );
};

// ---- Sub-components ----

const TierCard = ({ tier, loading, onCta }) => {
  const isHighlighted = tier.highlighted;
  const checkColor = isHighlighted ? T.orange : T.moss;

  return (
    <div style={{
      backgroundColor: T.card,
      borderRadius: T.r4,
      boxShadow: isHighlighted ? T.sh3 : T.sh2,
      display: 'flex',
      flexDirection: 'column',
      border: isHighlighted ? `1.5px solid ${T.orange}` : `1px solid ${T.rule}`,
    }}>
      {/* Card header */}
      <div style={{
        padding: tier.popular ? '12px 24px 16px' : '22px 24px 16px',
        borderBottom: `1px dashed ${T.rule}`,
        backgroundColor: isHighlighted ? T.roofSoft : T.paperSoft,
        borderRadius: `${T.r4} ${T.r4} 0 0`,
      }}>
        {/* Popular badge — inside header so it's never clipped */}
        {tier.popular && (
          <div style={{
            display: 'inline-block',
            backgroundColor: T.orange,
            color: '#fff',
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.06em',
            padding: '2px 10px',
            borderRadius: T.full,
            fontFamily: T.sans,
            marginBottom: 10,
          }}>
            MOST POPULAR
          </div>
        )}
        <div style={{
          fontSize: '12px',
          fontWeight: 800,
          color: T.inkMuted,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontFamily: T.sans,
          marginBottom: '10px',
        }}>
          {tier.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
          <span style={{
            fontSize: '44px',
            fontWeight: 800,
            color: T.ink,
            letterSpacing: '-1px',
            lineHeight: 1,
            fontFamily: T.sans,
          }}>
            {tier.price}
          </span>
          {tier.priceSuffix && (
            <span style={{ fontSize: '16px', color: T.inkMuted, fontFamily: T.sans }}>
              {tier.priceSuffix}
            </span>
          )}
        </div>
        <p style={{
          fontSize: '13px',
          color: T.ink2,
          lineHeight: 1.45,
          margin: 0,
          fontFamily: T.sans,
        }}>
          {tier.blurb}
        </p>
      </div>

      {/* Features */}
      <div style={{
        padding: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        flex: 1,
      }}>
        {tier.features.map((feat, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: checkColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <span style={{ fontSize: '14px', color: T.ink2, fontFamily: T.sans, lineHeight: 1.4 }}>
              {feat}
            </span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: '0 22px 22px' }}>
        <TierButton
          highlighted={isHighlighted}
          disabled={tier.ctaDisabled}
          onClick={onCta}
        >
          {loading ? 'Loading...' : tier.cta}
        </TierButton>
      </div>
    </div>
  );
};

const TierButton = ({ highlighted, disabled, onClick, children }) => {
  const [hovered, setHovered] = React.useState(false);

  const style = {
    width: '100%',
    padding: '11px 16px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: T.sans,
    borderRadius: T.r3,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'all 0.15s ease',
    border: 'none',
    ...(highlighted
      ? {
          backgroundColor: hovered && !disabled ? T.orangeDark : T.orange,
          color: '#fff',
        }
      : {
          backgroundColor: 'transparent',
          color: hovered && !disabled ? T.ink : T.ink2,
          border: `1.5px solid ${hovered && !disabled ? T.ruleStrong : T.rule}`,
        }),
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

const FaqItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div style={{
      backgroundColor: T.card,
      borderRadius: T.r3,
      border: `1px solid ${isOpen ? T.ruleStrong : T.rule}`,
      boxShadow: T.sh1,
      overflow: 'hidden',
      transition: 'border-color 0.15s ease',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '18px 22px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: T.sans,
        }}
      >
        <span style={{
          fontSize: '15px',
          fontWeight: 700,
          color: T.ink,
          lineHeight: 1.35,
        }}>
          {question}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={T.inkMuted}
          strokeWidth="2.5"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
            marginLeft: '14px',
          }}
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div style={{
          padding: '0 22px 18px',
          color: T.ink2,
          fontSize: '14px',
          lineHeight: 1.6,
          fontFamily: T.sans,
        }}>
          {answer}
        </div>
      )}
    </div>
  );
};

const CtaButton = ({ onClick, children }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '14px 28px',
        backgroundColor: hovered ? T.orangeDark : T.orange,
        color: '#fff',
        fontSize: '16px',
        fontWeight: 700,
        fontFamily: T.sans,
        border: 'none',
        borderRadius: T.r4,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

// Checkout Modal Component
const CheckoutModal = ({ clientSecret, stripePromise, onClose, planName }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(46,33,9,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: T.card,
          borderRadius: T.r4,
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: T.sh3,
          border: `1px solid ${T.rule}`,
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: `1px solid ${T.rule}`,
            backgroundColor: T.paperSoft,
          }}
        >
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 800,
              color: T.ink,
              fontFamily: T.sans,
            }}>
              Upgrade to {planName}
            </h2>
            <p style={{
              margin: '3px 0 0',
              fontSize: '13px',
              color: T.inkMuted,
              fontFamily: T.sans,
            }}>
              Complete your subscription
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: T.r2,
              color: T.inkMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = T.paperDeep}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <XMarkIcon size={22} />
          </button>
        </div>

        {/* Stripe Embedded Checkout */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
          }}
        >
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
